import { AsyncPipe, CommonModule, NgTemplateOutlet } from '@angular/common';
import {
  Component,
  Input,
  OnChanges,
  OnInit,
  OnDestroy,
  Output,
  SimpleChanges,
  EventEmitter,
  ViewChild,
  ContentChild,
  TemplateRef,
  inject,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIcon } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTableModule } from '@angular/material/table';
import { SepTableService } from './services/sep-table.service';
import { PrimaryButtonComponent } from '../primary-button/primary-button.component';
import { Column } from './Column';
import {
  TableBulkAction,
  TableSubAction,
  TableTargetedAction,
  TableToolbarAction,
} from './Action';
import {
  FilterCategory,
  SepFilterComponent,
} from '../sep-filter/sep-filter.component';
import { MatSort } from '@angular/material/sort';
import { TranslocoPipe } from '@jsverse/transloco';
import { LoadingComponent } from '../loading/loading.component';
import { ReactiveFormsModule, FormControl } from '@angular/forms';
import { InputFieldComponent } from '../input-field/input-field.component';
import { Subject, takeUntil } from 'rxjs';
import { MatTooltip } from '@angular/material/tooltip';

export enum SepTableType {
  SMALL = 'SMALL',
  LARGE = 'LARGE',
}

@Component({
  selector: 'app-sep-table',
  imports: [
    CommonModule,
    MatButtonModule,
    MatCheckboxModule,
    MatIcon,
    MatMenuModule,
    MatTableModule,
    NgTemplateOutlet,
    PrimaryButtonComponent,
    AsyncPipe,
    SepFilterComponent,
    TranslocoPipe,
    LoadingComponent,
    ReactiveFormsModule,
    InputFieldComponent,
    MatTooltip,
  ],
  providers: [SepTableService],
  templateUrl: './sep-table.component.html',
})
export class SepTableComponent<T> implements OnChanges, OnInit, OnDestroy {
  sepTableService = inject<SepTableService<T>>(SepTableService);

  sepTableType = SepTableType;
  @Input() columns: Array<Column<T, string | { $implicit: string }>> = [];
  @Input() dataSource: T[] = [];
  @Input() bulkActions: Array<TableBulkAction<T>> = [];
  @Input() bulkActionsNote?: string;
  @Input() rowActions: Array<TableTargetedAction<T>> = [];
  @Input() toolbarExportActions: TableToolbarAction[] = [];
  @Input() noDataText: string;
  @Input() name?: string;
  @Input() primaryButtonTitle?: string;
  @Input() filterCategories?: FilterCategory[];
  @Input() checkboxSelectedText?: string;
  @Input() hasCheckbox?: boolean = true;
  @Input() hasBulkActionButton?: boolean = false;
  @Input() hideColumnHeader?: boolean = false;
  @Input() type?: SepTableType = SepTableType.LARGE;
  @Input() allowExport = false;
  @Input() hidePrimaryButton = false;
  @Input() primaryButtonDisabled = false;
  @Input() primaryButtonDisabledTooltip?: string;
  @Input() loading = false;
  @Input() showSearch = false;
  @Input() searchValue = '';
  @Input() searchPlaceholder = 'general.searchEmployees';
  @ContentChild('toolbarLeft') toolbarLeftTemplate?: TemplateRef<void>;
  @Output() primaryButtonClickedEvent: EventEmitter<void> =
    new EventEmitter<void>();
  @Output() exportContentButtonClickedEvent: EventEmitter<void> =
    new EventEmitter<void>();
  @Output() searchChanged: EventEmitter<string> = new EventEmitter<string>();
  targetedRow: T | null = null;
  currentSortColumn: keyof T | null = null;
  sortOrderAscending = true;
  searchControl: FormControl<string> = new FormControl('', {
    nonNullable: true,
  });
  private readonly destroy$: Subject<void> = new Subject();

  @ViewChild(MatSort, { static: true }) sort: MatSort;

  ngOnInit(): void {
    this.searchControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.onSearchChanged(value);
      });
  }

  ngOnChanges(changes?: SimpleChanges): void {
    const dataSourceChange = changes?.['dataSource'];
    if (dataSourceChange) {
      this.sepTableService.setDataSource(dataSourceChange.currentValue as T[]);
    }

    const searchValueChange = changes?.['searchValue'];
    if (
      searchValueChange &&
      searchValueChange.currentValue !== this.searchControl.value
    ) {
      this.searchControl.setValue(searchValueChange.currentValue as string, {
        emitEvent: false,
      });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get displayedColumns(): string[] {
    const hasRowActions = this.rowActions.length;
    let columns = this.columns.map(({ headerName }) => headerName);
    if (this.hasCheckbox) {
      columns = ['select'].concat(columns);
    }
    if (hasRowActions) {
      columns.push('rowActions');
    }
    return columns;
  }

  toggleAllRows(): void {
    this.sepTableService.toggleAllRows();
  }

  selectRow(row: T): void {
    this.sepTableService.selectRow(row);
  }

  get selection$() {
    return this.sepTableService.selection$;
  }

  setTargetedRow(row: T) {
    this.targetedRow = row;
  }

  getLabel<TTarget>(
    action: TableTargetedAction<TTarget>,
    target: TTarget,
  ): string {
    return action.dynamicLabel ? action.dynamicLabel(target) : action.label;
  }

  getTooltipLabel<TTarget>(
    action: TableTargetedAction<TTarget>,
    target: TTarget,
  ): string {
    return action.tooltipLabel ? action.tooltipLabel(target) : '';
  }

  checkDisabled<TTarget>(
    action: TableTargetedAction<TTarget>,
    target: TTarget,
  ): boolean {
    return action.checkDisabled ? action.checkDisabled(target) : false;
  }

  checkHide<TTarget>(
    action: TableTargetedAction<TTarget>,
    target: TTarget,
  ): boolean {
    return action.checkHide ? action.checkHide(target) : false;
  }

  executeAction<TTarget>(
    action: TableTargetedAction<TTarget>,
    target: TTarget,
  ): void {
    action.executeAction?.(target);
  }

  executeSubAction<TTarget>(
    subAction: TableSubAction<TTarget>,
    target: TTarget,
  ): void {
    subAction.executeAction(target);
  }

  get visibleToolbarExportActions(): TableToolbarAction[] {
    return this.toolbarExportActions.filter(
      (action) => !this.checkHide(action, undefined),
    );
  }

  primaryButtonClicked(): void {
    this.primaryButtonClickedEvent.emit();
  }

  applyFilter(filterCategories: FilterCategory[]) {
    this.sepTableService.applyFilter(filterCategories);
  }

  sortColumn(columnKey: keyof T) {
    if (this.currentSortColumn === columnKey) {
      this.sortOrderAscending = !this.sortOrderAscending;
    } else {
      this.sortOrderAscending = true;
      this.currentSortColumn = columnKey;
    }
    this.sepTableService.sortOrGroupData(columnKey, this.sortOrderAscending);
  }
  exportContentButtonClicked(): void {
    this.exportContentButtonClickedEvent.emit();
  }

  onSearchChanged(value: string): void {
    this.searchChanged.emit(value);
  }
}
